import { Store } from "@tanstack/store";
import { useSelector } from "@tanstack/react-store";
import type { FlagState } from "./apply-review-state";

/** Keyed by local script id (not remoteId) since the phone's UI only ever
 * knows scripts by their local id. Module-level, no IndexedDB — a flag
 * banner is a live-session nicety, not data worth persisting offline. */
export const flagMessageStore = new Store<Record<string, FlagState>>({});

export function setFlag(scriptId: string, flag: FlagState | null): void {
  flagMessageStore.setState(state => {
    if (!flag || !flag.message) {
      if (!(scriptId in state)) return state;
      const next = { ...state };
      delete next[scriptId];
      return next;
    }
    return { ...state, [scriptId]: flag };
  });
}

/** Local, optimistic: flips a flag to acked ahead of the next poll
 * confirming it server-side. No-op if the flag already cleared (e.g. the
 * reviewer un-flagged it while the ack request was in flight). */
export function setFlagAckedLocally(scriptId: string, ackedAt: string, reply: string | null = null): void {
  flagMessageStore.setState(state => {
    const cur = state[scriptId];
    if (!cur) return state;
    return { ...state, [scriptId]: { ...cur, ackedAt, reply } };
  });
}

export function useFlagState(scriptId: string): FlagState | null {
  return useSelector(flagMessageStore, state => state[scriptId] ?? null);
}

/** Every script the desk currently has flagged, acked or not. The phone
 * keeps these in Home's "Flagged" section until the reviewer approves (the
 * poll clears the flag) — acknowledging only silences the nudge, it does
 * not mean the rework is done. */
export function useFlaggedIds(): string[] {
  return useSelector(flagMessageStore, state => Object.keys(state).filter(id => state[id]?.message));
}
